import { useMemo, useState } from 'react';
import { calcAge } from '@/lib/constants';
import { Heart, Check, MoreVertical } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import * as db from '@/lib/data-service';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth-context';
import CompatibilityTiles from '@/components/CompatibilityTiles';


export default function ProfileCard({ profile, onPromptLike, onReport, isOwnProfile = false }) {
  const [showReportMenu, setShowReportMenu] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);

  const REPORT_REASONS = ['Not Jain', 'Fake profile', 'Harassment', 'Inappropriate content'];

  const photos = profile.photos || [];
  const age = calcAge(profile.dob);
  const prompts = [
    { q: profile.prompt_1_q, a: profile.prompt_1_a },
    { q: profile.prompt_2_q, a: profile.prompt_2_a },
    { q: profile.prompt_3_q, a: profile.prompt_3_a },
  ].filter(p => p.q && p.a);
  const intents = Array.isArray(profile.relationship_intent)
    ? profile.relationship_intent
    : (profile.relationship_intent ? [profile.relationship_intent] : []);

  const displayName = profile.last_initial
    ? `${profile.display_name} ${profile.last_initial.charAt(0)}.`
    : profile.display_name;

  const handleReport = (reason) => {
    db.createReport(profile.id, profile.id, null, reason);
    setShowReportDialog(false);
    setShowReportMenu(false);
    setSelectedReason(null);
    toast('Report submitted. Thank you for helping keep Jiv safe.');
    if (onReport) onReport();
  };

  const { profile: myProfile } = useAuth();
  const traits = profile.personality_traits || [];
  const hobbies_list = profile.hobbies_list || [];

  const remainingPhotos = photos.slice(1);
  const maxRemaining = Math.max(remainingPhotos.length, prompts.length);
  const remainingSections = [];
  for (let i = 0; i < maxRemaining; i++) {
    if (i < remainingPhotos.length) remainingSections.push({ type: 'photo', photo: remainingPhotos[i], idx: i + 1 });
    if (i < prompts.length) remainingSections.push({ type: 'prompt', prompt: prompts[i], idx: i });
  }

  const badgeInfo = useMemo(() => {
    const s = profile.vouch_status || 'none';
    if (s === 'vouched')  return { label: 'Vouched',  color: '#C857A8' };
    if (s === 'verified') return { label: 'Verified', color: '#C857A8' };
    return null;
  }, [profile.vouch_status]);

  return (
    <div className="rounded-[20px] overflow-hidden bg-white dark:bg-card" data-testid="profile-card">

      {/* Section 1: Cover photo */}
      {photos.length > 0 ? (
        <div className="relative photo-gradient">
          <img
            src={photos[0].url}
            alt={`${profile.display_name} 1`}
            className="w-full h-auto block no-screenshot"
            data-testid="profile-photo-0"
            onContextMenu={e => { e.preventDefault(); return false; }}
            draggable={false}
            style={{ WebkitTouchCallout: 'none', userSelect: 'none', pointerEvents: 'auto' }}
          />

          {/* Vouch badge */}
          {badgeInfo && (
            <div
              style={{ background: 'rgba(255,255,255,0.9)' }}
              className="absolute top-3 right-3 flex items-center gap-1 rounded-[20px] px-2.5 py-1 z-20"
              data-testid="verification-badge"
            >
              <Check className="h-3 w-3" style={{ color: badgeInfo.color }} strokeWidth={3} />
              <span className="text-[10px] font-semibold" style={{ color: badgeInfo.color }}>{badgeInfo.label}</span>
            </div>
          )}

          {/* Report menu */}
          {!isOwnProfile && (
            <Sheet open={showReportMenu} onOpenChange={setShowReportMenu}>
              <SheetTrigger asChild>
                <button className="absolute top-3 left-3 z-20 w-8 h-8 rounded-full bg-black/20 flex items-center justify-center hover:bg-black/40 transition-colors">
                  <MoreVertical className="h-4 w-4 text-white" />
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="rounded-t-[20px]">
                <SheetHeader>
                  <SheetTitle>Report this person</SheetTitle>
                </SheetHeader>
                <div className="py-4 space-y-2">
                  {REPORT_REASONS.map(reason => (
                    <button
                      key={reason}
                      onClick={() => { setSelectedReason(reason); setShowReportDialog(true); }}
                      className="w-full text-left px-4 py-3 rounded-xl border border-border hover:bg-muted transition-colors text-sm"
                      data-testid={`profile-report-reason-${reason.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          )}

          {/* Name + age overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4 z-10" style={{ pointerEvents: 'none' }}>
            <div className="flex items-baseline gap-1.5">
              <span style={{ fontSize: 22, fontWeight: 500, color: 'white' }}>{displayName},</span>
              <span style={{ fontSize: 18, fontWeight: 400, color: 'white', opacity: 0.9 }}>{age}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-[#F0EDE9] aspect-[4/5] flex items-center justify-center">
          <span className="text-6xl font-semibold text-[#D3D0CB]">{profile.display_name?.[0]}</span>
        </div>
      )}

      {/* Cover photo caption */}
      {photos[0]?.caption && <p className="px-5 py-2 italic text-xs text-[#A8A5A0]">{photos[0].caption}</p>}

      {/* Section 2: Info chips */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex flex-wrap gap-2">
          {profile.city && <InfoChip>{profile.city}</InfoChip>}
          {profile.profession && <InfoChip>{profile.profession}</InfoChip>}
          {(profile.home_language || []).length > 0 && (
            <InfoChip>{(profile.home_language || []).join(' / ')}</InfoChip>
          )}
          {profile.sect && <InfoChip>{profile.sect}</InfoChip>}
          {profile.diet_practice && <InfoChip>{profile.diet_practice.split('(')[0].trim()}</InfoChip>}
          {profile.practice_level && <InfoChip>{profile.practice_level}</InfoChip>}
        </div>
      </div>

      {/* Section 3: Compatibility tiles */}
      {!isOwnProfile && (
        <div className="pt-1 pb-2">
          <CompatibilityTiles viewedProfile={profile} myProfile={myProfile} />
        </div>
      )}

      {/* Section 4: Remaining photos interleaved with prompts */}
      {remainingSections.map((sec) => {
        if (sec.type === 'photo') {
          return (
            <div key={`ph-${sec.idx}`}>
              <img
                src={sec.photo.url}
                alt={`${profile.display_name} ${sec.idx + 1}`}
                className="w-full h-auto block no-screenshot"
                data-testid={`profile-photo-${sec.idx}`}
                onContextMenu={e => { e.preventDefault(); return false; }}
                draggable={false}
                style={{ WebkitTouchCallout: 'none', userSelect: 'none', pointerEvents: 'auto' }}
              />
              {sec.photo.caption && <p className="px-5 py-2 italic text-xs text-[#A8A5A0]">{sec.photo.caption}</p>}
            </div>
          );
        }
        if (sec.type === 'prompt') {
          return (
            <div key={`pr-${sec.idx}`} className="px-4 py-3">
              <div className="relative p-4 bg-[#F7F5F2] rounded-[14px]" data-testid={`prompt-card-${sec.idx + 1}`}>
                <p className="text-[10px] uppercase tracking-[0.08em] text-[#A8A5A0] font-medium">{sec.prompt.q}</p>
                <p className="text-[15px] text-[#1A1814] mt-1.5 leading-[1.6]">{sec.prompt.a}</p>
                {onPromptLike && (
                  <button
                    onClick={e => { e.stopPropagation(); onPromptLike(sec.idx + 1); }}
                    className="absolute bottom-3 right-3 prompt-heart"
                    data-testid={`prompt-like-${sec.idx + 1}`}
                  >
                    <Heart className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          );
        }
        return null;
      })}

      {/* Section 5: Personality traits */}
      {traits.length > 0 && (
        <div className="px-4 pt-4 pb-2">
          <p className="text-[10px] uppercase tracking-[0.08em] text-[#A8A5A0] font-semibold mb-2">Personality</p>
          <div className="flex flex-wrap gap-2">
            {traits.map(t => <InfoChip key={t}>{t}</InfoChip>)}
          </div>
        </div>
      )}

      {/* Section 6: Hobbies (Into) */}
      {hobbies_list.length > 0 && (
        <div className="px-4 pt-3 pb-4">
          <p className="text-[10px] uppercase tracking-[0.08em] text-[#A8A5A0] font-semibold mb-2">Into</p>
          <div className="flex flex-wrap gap-2">
            {hobbies_list.map(h => <InfoChip key={h}>{h}</InfoChip>)}
          </div>
        </div>
      )}

      {/* Looking for */}
      {intents.length > 0 && (
        <div className="px-5 pt-4 pb-5">
          <p className="text-[10px] uppercase tracking-[0.08em] text-[#A8A5A0] font-semibold mb-2">Looking for</p>
          <div className="flex flex-wrap gap-1.5">
            {intents.map(ri => <InfoChip key={ri}>{ri}</InfoChip>)}
          </div>
        </div>
      )}

      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle>Confirm report</DialogTitle>
            <DialogDescription>You're reporting this person for: <strong>{selectedReason}</strong></DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowReportDialog(false); setSelectedReason(null); }} className="rounded-xl">Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => selectedReason && handleReport(selectedReason)}
              className="rounded-xl"
              data-testid="confirm-profile-report-btn"
            >
              Submit report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoChip({ children }) {
  return (
    <span
      style={{
        fontSize: 12,
        padding: '4px 10px',
        borderRadius: 20,
        border: '0.5px solid rgba(0,0,0,0.12)',
        background: 'white',
        color: '#1A1814',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}
